import React from "react";
import { Helmet } from "react-helmet-async";
import SitePagesMetadata from '/src/Components/HelmetProvider/SitePagesMetadata.json';
/**
 * HelmetProvider - A systematic component for managing page metadata
 * @param {Object} props - Component props
 * @param {string} props.pageType - The type of page (e.g., 'home', 'property', 'blog', 'about')
 * @param {string} [props.pageId] - Optional page identifier (e.g., property address, blog post slug) - used for titles, not URLs
 * @param {Object} [props.customMeta] - Optional custom metadata to override defaults
 * @param {string} [props.customTitle] - Optional custom title to override default
 * @param {string} [props.customDescription] - Optional custom description to override default
 * @param {boolean} [props.isLoading] - Optional loading state to prevent rendering
 * @param {Object} [props.structuredData] - Optional structured data object to render
 */
const HelmetProvider = ({
  pageType,
  pageId = null,
  customMeta = {},
  customTitle = null,
  customDescription = null,
  isLoading = false,
  structuredData = null
}) => {
  // Don't render if still loading
  if (isLoading) {
    return null;
  }

  const getSitePagesMetadataObject = () => {
    return SitePagesMetadata;
  }

  const sitePagesMetadataObject = getSitePagesMetadataObject();
  const standardPageTitleEnding = sitePagesMetadataObject?.standard_ending?.title || "| Quoll";

  // Base metadata configuration - updated to match index.html
  const baseMeta = {
    siteName: "Quoll",
    baseUrl: "https://getquoll.com", // Updated to match your actual domain
    defaultTitle: "Quoll: Your Home's Climate Playbook",
    defaultDescription: "Understand your home's climate risks, what they'll cost you, and what you can do to protect your wallet. Quoll is here to make your home and wallet resilient!",
    defaultImage: "https://d2dhzsfgxco53w.cloudfront.net/Quoll-Wordmark.svg", // Updated to match your actual image
    twitterHandle: "@Quoll_Intel", // Update with your actual Twitter handle if different
  };

  // Page-specific metadata configurations - updated to match your existing titles
  // const titleEnding = " | Quoll"
  // const pageConfigs = {
  //   home: {
  //     title: "Quoll: Your Home's Climate Playbook",
  //     description: "Understand your home's climate risks, what they'll cost you, and what you can do to protect your wallet. Quoll is here to make your home and wallet resilient!",
  //     keywords: "climate change, financial risk, home improvement, climate risks, property protection",
  //     ogType: "website",
  //   },
  //   property: {
  //     title: pageId ? `${pageId} | Quoll Property Analysis` : "Property Analysis | Quoll",
  //     description: pageId
  //       ? `Get detailed climate risk analysis and resilience recommendations for ${pageId}. Understand what climate risks will cost you and how to protect your investment.`
  //       : "Analyze your property's climate risks and get personalized recommendations to protect your home and investment. Understand what climate risks will cost you.",
  //     keywords: "property analysis, climate risk assessment, home improvement, property value, climate resilience, financial risk",
  //     ogType: "website",
  //   },
  //   blog: {
  //     title: pageId ? `${pageId} | Quoll Resources` : "Quoll Resources",
  //     description: pageId
  //       ? "Read our latest insights on climate risk, property intelligence, and home protection."
  //       : "Stay informed about climate risks, property intelligence, and home protection strategies with Quoll's expert insights.",
  //     keywords: "climate blog, property intelligence, home protection, climate risk insights, financial risk",
  //     ogType: "article",
  //   },
  //   whatIsQuoll: {
  //     title: "What is Quoll?" + titleEnding,
  //     description: "Learn how Quoll translates climate risk into actionable intelligence for homeowners. Protect your home with our comprehensive property analysis platform.",
  //     keywords: "about Quoll, climate intelligence, property platform, home protection, what is Quoll",
  //     ogType: "website",
  //   },
  //   whatWeDo: {
  //     title: "Methodology" + titleEnding,
  //     description: "Discover how Quoll helps homeowners understand and protect against climate risks. Learn about our comprehensive property intelligence platform.",
  //     keywords: "what we do, Quoll services, climate risk analysis, property intelligence, home protection",
  //     ogType: "website",
  //   },
  //   whyFree: {
  //     title: "Why Is Quoll Free?" + titleEnding,
  //     description: "Learn why Quoll provides free climate risk intelligence to homeowners. Understand our mission to protect homes and wallets from climate risks.",
  //     keywords: "why free, Quoll pricing, free property analysis, climate risk assessment, home protection",
  //     ogType: "website",
  //   },
  //   resources: {
  //     title: "Resources" + titleEnding,
  //     description: "Access over 500 blogs and videos covering climate risks, resilience home improvements, how-to guides, and financial explainers. Find materials specific to your property.",
  //     keywords: "climate resources, property guides, home protection tools, climate education, how-to guides, financial explainers",
  //     ogType: "website",
  //   },
  //   contact: {
  //     title: "Contact Us" + titleEnding,
  //     description: "Get in touch with the Quoll team for questions about climate intelligence, property analysis, or partnership opportunities.",
  //     keywords: "contact Quoll, climate intelligence support, property analysis help, customer support",
  //     ogType: "website",
  //   },
  //   dashboard: {
  //     title: "Dashboard" + titleEnding,
  //     description: "Access your personalized Quoll dashboard to view your property analysis, climate risk assessments, and improvement recommendations.",
  //     keywords: "Quoll dashboard, property dashboard, climate risk dashboard, personal property analysis",
  //     ogType: "website",
  //   },
  //   error: {
  //     title: "Error" + titleEnding,
  //     description: "We encountered an error. Please try again or contact our support team for assistance.",
  //     keywords: "error, Quoll support, technical issue",
  //     ogType: "website",
  //   },
  //   faq: {
  //     title: "Frequently Asked Questions" + titleEnding,
  //     description: "Frequently asked questions about Quoll. Learn about our mission, how it works, and how we're different.",
  //     keywords: "FAQ, Quoll, climate risk, property analysis, home protection, frequently asked questions, faq",
  //     ogType: "website",
  //   },
  //   workWithUs: {
  //     title: "Careers" + titleEnding,
  //     description: "Join the Quoll team and help us make a difference in the fight against climate change. We're looking for talented individuals who are passionate about climate risk and want to make a difference.",
  //     keywords: "work with us, Quoll team, climate risk, climate change, home improvement",
  //     ogType: "website",
  //   },
  //   termsOfService: {
  //     title: "Terms of Service" + titleEnding,
  //     description: "Read our Terms of Service for Quoll. Understand how we use your data and how we protect your privacy.",
  //     keywords: "terms of service, Quoll privacy, Quoll data use, Quoll terms",
  //     ogType: "website",
  //   },
  //   privacyPolicy: {
  //     title: "Privacy Policy" + titleEnding,
  //     description: "Read our Privacy Policy for Quoll. Understand how we use your data and how we protect your privacy.",
  //     keywords: "privacy policy, Quoll privacy, Quoll data use, Quoll terms",
  //     ogType: "website",
  //   },
  //   signIn: {
  //     title: "Sign In" + titleEnding,
  //     description: "Sign in to your Quoll account to access your property analysis, climate risk assessments, and improvement recommendations.",
  //     keywords: "sign in, Quoll account, property analysis, climate risk assessment, improvement recommendations",
  //     ogType: "website",
  //   },
  //   signUp: {
  //     title: "Sign Up" + titleEnding,
  //     description: "Sign up for a Quoll account to access your property analysis, climate risk assessments, and improvement recommendations.",
  //     keywords: "sign up, Quoll account, property analysis, climate risk assessment, improvement recommendations",
  //     ogType: "website",
  //   },
  // };

  // Get the configuration for the current page type
  const pageConfig = { ...sitePagesMetadataObject[pageType] } || { ...sitePagesMetadataObject.home };

  // If Blog or Property then we need to update the title to pageId
  if ((pageType === 'blog' || pageType === 'property') && pageId) {
    pageConfig.title = `${pageId}`;
  }

  // Add the standard page title ending to the title
  pageConfig.title = `${pageConfig.title} ${standardPageTitleEnding}`;

  // Ensure that there is only one instance of the standard page title ending, so we need to count the number of instances of the standard page title ending and remove the extra ones

  // Merge custom metadata with page configuration
  const finalTitle = customTitle || pageConfig.title;
  const finalDescription = customDescription || pageConfig.description;
  const finalMeta = { ...pageConfig, ...customMeta };

  // Build the canonical URL using the actual current path
  const canonicalUrl = `${baseMeta.baseUrl}${window.location.pathname}`;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{finalTitle}</title>
      <meta name="description" content={finalDescription} />
      <meta name="keywords" content={finalMeta.keywords} />
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph Meta Tags */}
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDescription} />
      <meta property="og:type" content={finalMeta.ogType} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:site_name" content={baseMeta.siteName} />
      <meta property="og:image" content={finalMeta.ogImage || baseMeta.defaultImage} />
      <meta property="og:image:alt" content={finalTitle} />

      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={baseMeta.twitterHandle} />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDescription} />
      <meta name="twitter:image" content={finalMeta.ogImage || baseMeta.defaultImage} />
      <meta name="twitter:image:alt" content={finalTitle} />

      {/* Additional Meta Tags */}
      <meta name="robots" content="index, follow" />
      {/* <meta name="author" content="Quoll Intelligence" /> */}

      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData, null, 2)}
        </script>
      )}

      {/* Viewport and Mobile Meta Tags */}
      {/* <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta name="theme-color" content="#ffffff" /> Update with your brand color */}

      {/* Additional custom meta tags if provided */}
      {Object.entries(finalMeta).map(([key, value]) => {
        // Skip keys we've already handled
        if (["title", "description", "keywords", "ogType", "ogImage"].includes(key)) {
          return null;
        }

        // Handle custom meta tags
        if (key.startsWith("og:")) {
          return <meta key={key} property={key} content={value} />;
        } else if (key.startsWith("twitter:")) {
          return <meta key={key} name={key} content={value} />;
        } else {
          return <meta key={key} name={key} content={value} />;
        }
      })}
    </Helmet>
  );
};

export default HelmetProvider;